import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _omitBy from 'lodash/omitBy';

import Validate from 'libs/validate';

import {
  DEFAULT_VIDEO_SOURCE_OPTIONS,
  SOURCE_VIMEO,
  SOURCE_YOUTUBE,
  SOURCE_OTHER,
  VIDEO_SOURCES_OPTIONS,
} from './VideoPlayerEditor.constants';

// YouTube regex patterns
export const YOUTUBE_URL_PATTERN = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)/;
export const YOUTUBE_EMBED_PATTERN =
  /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/;

// Vimeo regex patterns
export const VIMEO_URL_PATTERN = /^(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com/;
export const VIMEO_PLAYER_PATTERN = /^(?:https?:\/\/)?player\.vimeo\.com\/video\/([0-9]+)/;

// URL validation pattern
export const VALID_URL_PATTERN =
  /^(?:https?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=]+$/;

export function getVideoSourceOptions(givenOptions = []) {
  const definedGivenOptions = givenOptions || [];
  const options = VIDEO_SOURCES_OPTIONS.filter((option) =>
    definedGivenOptions.includes(option.value),
  );
  return _isEmpty(options) ? DEFAULT_VIDEO_SOURCE_OPTIONS : options;
}

export function getUrlLabel(source) {
  let label = 'URL';
  switch (source) {
    case SOURCE_YOUTUBE:
      label += ' or YouTube Video ID';
      break;
    case SOURCE_VIMEO:
      label += ' or Vimeo Video ID';
      break;
    default:
      break;
  }
  return label;
}

export function calculateUrlAndId(source, value) {
  const isValidUrl = Validate.validate(Validate.TYPES.LINK, value);
  if (source === SOURCE_OTHER || isValidUrl) {
    return { url: value, videoId: undefined };
  }
  return { url: undefined, videoId: value };
}

export function normalizeVideoData(videoData = {}) {
  const { source, url, videoId, ...rest } = videoData;
  const value = videoId || url;

  if (_isEmpty(value)) {
    return _omitBy({ source, ...rest }, _isNil);
  }

  return _omitBy({ source, ...rest, ...calculateUrlAndId(source, value) }, _isNil);
}

export function extractYoutubeId(url) {
  if (!url) return undefined;

  // Match patterns like youtube.com/watch?v=ID or youtu.be/ID
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return match && match[2].length === 11 ? match[2] : undefined;
}

export function extractVimeoId(url) {
  if (!url) return undefined;

  // Match patterns like vimeo.com/ID or player.vimeo.com/video/ID
  const regExp = /^.*(vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/;
  const match = url.match(regExp);

  return match && match[2] ? match[2] : undefined;
}

export function calculateEmbedParams(videoId, source, url) {
  if (source === SOURCE_OTHER) {
    return { url };
  }

  if (!videoId) {
    if (source === SOURCE_YOUTUBE) {
      const extractedId = extractYoutubeId(url);
      if (extractedId) {
        return { id: extractedId, source };
      }
    }
    if (source === SOURCE_VIMEO) {
      const extractedId = extractVimeoId(url);
      if (extractedId) {
        return { id: extractedId, source };
      }
    }
    return { url };
  }

  return { id: videoId, source };
}

export function getSourceFromUrl(url) {
  if (!url) {
    return SOURCE_OTHER;
  }

  if (YOUTUBE_URL_PATTERN.test(url)) {
    return SOURCE_YOUTUBE;
  }

  if (VIMEO_URL_PATTERN.test(url)) {
    return SOURCE_VIMEO;
  }

  return SOURCE_OTHER;
}

export function validateVideoURL(url, source = SOURCE_OTHER) {
  if (!url) {
    return false;
  }

  switch (source) {
    case SOURCE_YOUTUBE:
      return YOUTUBE_EMBED_PATTERN.test(url);

    case SOURCE_VIMEO:
      return VIMEO_PLAYER_PATTERN.test(url);

    case SOURCE_OTHER:
      return Validate.validate(Validate.TYPES.LINK, url);

    default:
      return false;
  }
}

export function isVideoUrlValid(source, url) {
  if (!url) {
    return true;
  }

  return getSourceFromUrl(url) === source && validateVideoURL(url, source);
}

export function getVideoUrlValidationMessage(isSameSource, isValidSourceUrl, source) {
  if (!isSameSource) {
    return 'The URL you entered is not a valid URL for the selected source.';
  }
  if (!isValidSourceUrl) {
    let mainErrorMessage = 'The URL you entered is not a valid URL.';
    if (source === SOURCE_YOUTUBE) {
      mainErrorMessage +=
        ' The URL should have this shape https://www.youtube.com/embed/<VIDEO_ID>';
    } else if (source === SOURCE_VIMEO) {
      mainErrorMessage +=
        ' The URL should have this shape https://player.vimeo.com/video/<VIDEO_ID>';
    }
    return mainErrorMessage;
  }
  return '';
}
